/* EXC cart drawer: savings line on each item
   Shows the higher of the item's compare-at price and its pre-discount price
   struck through, the price actually being paid, and "Save $X!" in green,
   only when there is a real saving. The cart API does not include compare-at
   prices, so each product's own JSON is read once and cached.
   The theme redraws cart items whenever the cart changes, so the drawer is
   watched and the line is put back after each redraw. It writes only when the
   numbers differ from what is already shown, so its own edits do not set it
   off again.
   The cart itself is read from the theme's cart store, which already holds the
   cart the drawer was just drawn from. This used to fetch /cart.js on every
   redraw, which was 2 to 4 extra cart requests per add to cart.
   Styles: assets/exc-cart.css. Notes: hq/projects/excdesigns/theme-build-notes.md */
(function () {
  var productCache = {};
  var busy = false;
  var queued = false;
  var timer = null;
  var storeP = null;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // The store's cart, or a real fetch if the store is not there or not ready
  function getCart() {
    if (!storeP) storeP = import('cart-store').then(function (m) { return m.default; });
    return storeP
      .then(function (Cart) {
        var cart = Cart.getState().cart;
        return cart && cart.items ? cart : fetchCart();
      })
      .catch(fetchCart);
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function product(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function apply() {
    var drawer = document.getElementById('drawer-cart');
    if (!drawer) return;
    if (busy) { queued = true; return; }
    busy = true;

    getCart()
      .then(function (cart) {
        var byKey = {};
        cart.items.forEach(function (item) { byKey[item.key] = item; });

        var rows = drawer.querySelectorAll('cart-item[key]');
        return Promise.all(Array.prototype.map.call(rows, function (row) {
          var item = byKey[row.getAttribute('key')];
          var priceBox = row.querySelector('.cart-item__price');
          if (!item || !priceBox) return null;

          return product(item.handle).then(function (p) {
            var compareEach = 0;
            if (p && p.variants) {
              var v = p.variants.filter(function (x) { return x.id === item.variant_id; })[0];
              if (v && v.compare_at_price) compareEach = v.compare_at_price;
            }
            var was = Math.max(item.original_line_price, compareEach * item.quantity);
            var now = item.final_line_price;
            var existing = priceBox.querySelector('.exc-cart-price');

            if (was <= now) {
              if (existing) existing.remove();
              return;
            }

            var signature = was + '|' + now;
            if (existing && existing.getAttribute('data-sig') === signature) return;

            var el = existing || document.createElement('div');
            el.className = 'exc-cart-price';
            el.setAttribute('data-sig', signature);
            el.innerHTML =
              '<span class="exc-cart-price__pair">' +
              '<span class="exc-cart-price__was"><span class="u-hidden-visually">Was </span>' + money(was, cart.currency) + '</span>' +
              '<span class="exc-cart-price__now"><span class="u-hidden-visually">Now </span>' + money(now, cart.currency) + '</span>' +
              '</span>' +
              '<span class="exc-cart-price__save">Save ' + money(was - now, cart.currency) + '!</span>';
            if (!existing) priceBox.appendChild(el);
          });
        }));
      })
      .catch(function () {})
      .then(function () {
        busy = false;
        if (queued) { queued = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(apply, 120);
  }

  function watch() {
    var drawer = document.getElementById('drawer-cart');
    if (!drawer) return;
    new MutationObserver(function (records) {
      // Ignore changes made inside our own savings line, and the insert of the
      // line itself: appending it is a change to .cart-item__price, which used to
      // count as a redraw and set off one more pass every time a line went in
      var relevant = records.some(function (r) {
        if (r.target.closest && r.target.closest('.exc-cart-price')) return false;
        var nodes = Array.prototype.slice.call(r.addedNodes).concat(Array.prototype.slice.call(r.removedNodes));
        var onlyOurs = nodes.length && nodes.every(function (n) {
          return n.classList && n.classList.contains('exc-cart-price');
        });
        return !onlyOurs;
      });
      if (relevant) schedule();
    }).observe(drawer, { childList: true, subtree: true });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();

/* EXC cart drawer: tick boxes on "Pairs with your order"
   Each recommendation card gets a tick box instead of "+ Add to cart".
   Ticking adds the product through the card's own theme add form, so the
   cart updates exactly as the button did. Unticking removes that product's
   line through the theme's cart store. A product with options opens the
   theme's quick view to pick one, like the old "Quick view" link.
   The theme normally drops a recommendation once it is in the cart; products
   ticked here are kept in the row (see the EXC note in
   assets/cart-recommendations.js) so the box stays visible and ticked.
   Styles: assets/exc-cart.css. */
(function () {
  var ticked = window.excCartTicked = window.excCartTicked || new Set();
  var cartStore = null;
  var cartProducts = new Set();

  function loadStore() {
    if (!cartStore) {
      cartStore = import('cart-store').then(function (m) { return m.default; });
    }
    return cartStore;
  }

  var seenInCart = new Set();

  function syncFromCart(cart) {
    cartProducts = new Set(cart.items.map(function (i) { return String(i.product_id); }));
    // A ticked product can leave the row again once it has been in the cart and
    // is gone (taken out with the bin). Not before: the cart reports its state
    // a moment before the add lands, and clearing then made the card vanish.
    Array.from(ticked).forEach(function (id) {
      if (cartProducts.has(id)) {
        seenInCart.add(id);
      } else if (seenInCart.has(id)) {
        ticked.delete(id);
        seenInCart.delete(id);
      }
    });
    decorate();
  }

  function decorate() {
    var cards = document.querySelectorAll('#modal-cart cart-recommendations product-card-mini[product-id]');
    Array.prototype.forEach.call(cards, function (card) {
      var id = card.getAttribute('product-id');
      var holder = card.querySelector('product-card-button');
      if (!holder) return;
      var box = holder.querySelector('.exc-rec-tick');
      if (!box) {
        box = document.createElement('label');
        box.className = 'exc-rec-tick';
        box.innerHTML = '<input type="checkbox" class="exc-rec-tick__input"><span class="exc-rec-tick__box" aria-hidden="true"></span><span class="exc-rec-tick__text">Add</span>';
        holder.appendChild(box);
        box.querySelector('input').addEventListener('change', function (e) { onToggle(card, e.target); });
      }
      var input = box.querySelector('input');
      var inCart = cartProducts.has(id);
      if (!box.classList.contains('is-busy')) input.checked = inCart;
      box.querySelector('.exc-rec-tick__text').textContent = inCart ? 'Added' : 'Add';
      var soldOut = holder.querySelector('button[disabled]');
      input.disabled = !!soldOut;
      holder.classList.add('has-exc-tick');
    });
  }

  function onToggle(card, input) {
    var id = card.getAttribute('product-id');
    var box = input.closest('.exc-rec-tick');
    box.classList.add('is-busy');

    if (input.checked) {
      ticked.add(id);
      var addButton = card.querySelector('product-form button[name="add"]');
      var quickShop = card.querySelector('[data-trigger-quick-shop]');
      if (addButton) {
        addButton.closest('form').requestSubmit(addButton);
      } else if (quickShop) {
        quickShop.click();
      }
      // The cart update redraws the state; drop the busy flag once it lands
      setTimeout(function () { box.classList.remove('is-busy'); decorate(); }, 2500);
      return;
    }

    loadStore().then(function (Cart) {
      var state = Cart.getState();
      var lines = state.cart.items.filter(function (i) { return String(i.product_id) === id; });
      var updates = {};
      lines.forEach(function (l) { updates[l.key] = 0; });
      return Cart.update({ updates: updates });
    }).catch(function () {
      input.checked = true;
    }).then(function () {
      box.classList.remove('is-busy');
      decorate();
    });
  }

  function start() {
    if (!document.getElementById('modal-cart')) return;
    loadStore().then(function (Cart) {
      syncFromCart(Cart.getState().cart);
      Cart.subscribe(function (state) { syncFromCart(state.cart); });
    }).catch(function () {});
    // Watch the whole drawer, not the recommendations row: with an empty cart
    // at page load the row does not exist yet and is drawn in later
    var drawer = document.getElementById('modal-cart');
    new MutationObserver(function (records) {
      var relevant = records.some(function (r) {
        return !(r.target.closest && r.target.closest('.exc-rec-tick')) &&
          r.target.closest && r.target.closest('cart-recommendations, interactive-cart');
      });
      if (relevant) decorate();
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* EXC cart drawer: "Pairs with your order" arrows loop
   The theme stops at the ends: it disables the next arrow on the last card and
   the previous arrow on the first. Here both arrows stay live, next on the last
   card slides back to the first, and previous on the first slides to the last.
   The click is caught before the theme's own arrow handler, and only at an end;
   in the middle of the row the theme moves the cards as usual. */
(function () {
  function row(button) {
    var scroller = button.closest('items-scroll');
    return scroller && scroller.querySelector('[data-items]');
  }

  function keepEnabled(root) {
    root.querySelectorAll('.navigation__button[disabled]').forEach(function (b) {
      b.disabled = false;
    });
  }

  function start() {
    var drawer = document.getElementById('modal-cart');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      var button = e.target.closest && e.target.closest('cart-recommendations .navigation__button');
      if (!button) return;
      var items = row(button);
      if (!items || items.children.length < 2) return;

      var max = items.scrollWidth - items.clientWidth;
      // Judge the ends by which card is in view, not by the raw scroll
      // position: cards snap into place a few pixels short of the true end
      var rowLeft = items.getBoundingClientRect().left;
      var inView = function (card) {
        var left = card.getBoundingClientRect().left - rowLeft;
        return left >= 0 && left < items.clientWidth / 2;
      };
      var atStart = inView(items.firstElementChild);
      var atEnd = inView(items.lastElementChild);
      var next = button.classList.contains('navigation__button--next');

      if (next && atEnd) {
        e.stopImmediatePropagation();
        e.preventDefault();
        items.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (!next && atStart) {
        e.stopImmediatePropagation();
        e.preventDefault();
        items.scrollTo({ left: max, behavior: 'smooth' });
      }
    }, true);

    // The theme switches the end arrows off as the row scrolls; switch them back on
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        if (r.attributeName === 'disabled' && r.target.disabled && r.target.closest('cart-recommendations')) {
          r.target.disabled = false;
        }
      });
    }).observe(drawer, { subtree: true, attributes: true, attributeFilter: ['disabled'] });
    keepEnabled(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
